// SPDX-License-Identifier: AGPL-3.0-or-later

import {ValidationErrorCodes} from '@fluxer/constants/src/ValidationErrorCodes';
import {LocaleSchema} from '@fluxer/schema/src/primitives/LocaleSchema';
import {createStringType} from '@fluxer/schema/src/primitives/SchemaPrimitives';
import {URLType} from '@fluxer/schema/src/primitives/UrlValidators';
import {z} from 'zod';

export const AVATAR_DECORATION_ID_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const MAX_AVATAR_DECORATION_CATALOG_ENTRIES = 100;
export const AVATAR_DECORATION_IMAGE_EXTENSIONS = ['.png', '.apng', '.webp', '.gif'] as const;
export const AVATAR_DECORATION_VIDEO_EXTENSIONS = ['.webm'] as const;

export type AvatarDecorationMediaKind = 'image' | 'video';

export const AvatarDecorationIdType = createStringType(1, 64).pipe(
	z.string().regex(AVATAR_DECORATION_ID_REGEX, ValidationErrorCodes.INVALID_FORMAT),
);

export function slugifyAvatarDecorationId(raw: string): string | null {
	const slug = raw
		.trim()
		.toLowerCase()
		.replace(/['’]/g, '')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
	if (slug.length < 1 || slug.length > 64 || !AVATAR_DECORATION_ID_REGEX.test(slug)) {
		return null;
	}
	return slug;
}

export const AvatarDecorationIdParam = z.object({
	id: AvatarDecorationIdType.describe('Hosted avatar decoration catalog id'),
});

export type AvatarDecorationIdParam = z.infer<typeof AvatarDecorationIdParam>;

const CatalogNameLocalizations = z
	.record(LocaleSchema, createStringType(1, 64))
	.optional()
	.describe('Optional per-locale display names keyed by UI locale');
const CatalogDescriptionLocalizations = z
	.record(LocaleSchema, createStringType(1, 256))
	.optional()
	.describe('Optional per-locale subtitles keyed by UI locale');

export function getAvatarDecorationMediaPath(imageUrl: string): string {
	try {
		return new URL(imageUrl).pathname.toLowerCase();
	} catch {
		return '';
	}
}

export function getAvatarDecorationMediaKind(imageUrl: string): AvatarDecorationMediaKind | null {
	const pathname = getAvatarDecorationMediaPath(imageUrl);
	if (AVATAR_DECORATION_VIDEO_EXTENSIONS.some((extension) => pathname.endsWith(extension))) {
		return 'video';
	}
	if (AVATAR_DECORATION_IMAGE_EXTENSIONS.some((extension) => pathname.endsWith(extension))) {
		return 'image';
	}
	return null;
}

function hasAllowedAvatarDecorationMediaExtension(imageUrl: string): boolean {
	return getAvatarDecorationMediaKind(imageUrl) !== null;
}

function sanitizeLocaleMap(raw: unknown, maxLength: number): Record<string, string> | undefined {
	if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
		return undefined;
	}
	const localizations: Record<string, string> = {};
	for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
		if (!LocaleSchema.safeParse(key).success || typeof value !== 'string') {
			continue;
		}
		const trimmed = value.trim();
		if (trimmed.length < 1 || trimmed.length > maxLength) {
			continue;
		}
		localizations[key] = trimmed;
	}
	return Object.keys(localizations).length > 0 ? localizations : undefined;
}

export function resolveAvatarDecorationLocalizedText(
	locale: string | null | undefined,
	fallback: string,
	localizations?: Record<string, string> | null,
): string {
	if (!localizations) {
		return fallback;
	}
	const requested = locale?.trim();
	if (requested && localizations[requested]) {
		return localizations[requested];
	}
	const language = requested?.split('-')[0];
	if (language && localizations[language]) {
		return localizations[language];
	}
	if (localizations['en-US']) {
		return localizations['en-US'];
	}
	if (localizations.en) {
		return localizations.en;
	}
	return fallback;
}

export const AvatarDecorationCatalogEntryResponse = z
	.object({
		id: AvatarDecorationIdType.describe('Stable slug for this avatar decoration'),
		name: createStringType(1, 64).describe('Display name shown in the avatar decoration picker'),
		description: createStringType(1, 256)
			.optional()
			.describe('Optional subtitle shown under the avatar decoration name'),
		image: URLType.describe('HTTPS media URL for the avatar decoration (PNG, APNG, WebP, GIF, or WebM)'),
		fx: z
			.string()
			.regex(/^[a-z0-9-]{1,32}$/)
			.optional()
			.describe('Built-in animated renderer key; clients that know it draw the effect instead of the image'),
		poster: URLType.optional().describe('Small static first frame shown until the decoration is active'),
		name_localizations: CatalogNameLocalizations,
		description_localizations: CatalogDescriptionLocalizations,
		accessible: z.boolean().optional().describe('Whether the current catalog still allows selecting this decoration'),
		pro_included: z.boolean().optional().describe('Included with an active Pro membership; usable without buying it'),
		price: z
			.string()
			.regex(/^[0-9]{1,7}(?:\.[0-9]{1,2})?$/)
			.optional()
			.describe('Store price (TRY); items with a price can be bought in the store'),
		shopier_product_id: z
			.string()
			.regex(/^[0-9]{1,20}$/)
			.optional()
			.describe('Shopier product that sells this item in the store'),
	})
	.refine((entry) => hasAllowedAvatarDecorationMediaExtension(entry.image), ValidationErrorCodes.INVALID_URL_FORMAT);

export type AvatarDecorationCatalogEntryResponse = z.infer<typeof AvatarDecorationCatalogEntryResponse>;

export const AvatarDecorationCatalogResponse = z.object({
	avatar_decorations: z
		.array(AvatarDecorationCatalogEntryResponse)
		.max(MAX_AVATAR_DECORATION_CATALOG_ENTRIES)
		.describe('Hosted avatar decoration catalog entries'),
});

export type AvatarDecorationCatalogResponse = z.infer<typeof AvatarDecorationCatalogResponse>;

export function sanitizeAvatarDecorationCatalog(input: unknown): Array<AvatarDecorationCatalogEntryResponse> {
	if (input == null || typeof input !== 'object') {
		return [];
	}
	const rawObject = input as {avatar_decorations?: unknown; decorations?: unknown};
	const rawEntries = rawObject.avatar_decorations ?? rawObject.decorations;
	if (!Array.isArray(rawEntries)) {
		return [];
	}
	const seenIds = new Set<string>();
	const decorations: Array<AvatarDecorationCatalogEntryResponse> = [];
	for (const rawEntry of rawEntries) {
		if (decorations.length >= MAX_AVATAR_DECORATION_CATALOG_ENTRIES) {
			break;
		}
		const rawRecord = rawEntry != null && typeof rawEntry === 'object' ? (rawEntry as Record<string, unknown>) : null;
		const slugifiedId = typeof rawRecord?.id === 'string' ? slugifyAvatarDecorationId(rawRecord.id) : null;
		const parsed = AvatarDecorationCatalogEntryResponse.safeParse({
			...(rawRecord ?? {}),
			id: slugifiedId ?? rawRecord?.id,
			name_localizations: undefined,
			description_localizations: undefined,
		});
		if (!parsed.success) {
			continue;
		}
		if (seenIds.has(parsed.data.id)) {
			continue;
		}
		seenIds.add(parsed.data.id);
		const rawLocalizations = rawRecord ?? {};
		const nameLocalizations = sanitizeLocaleMap(rawLocalizations.name_localizations, 64);
		const descriptionLocalizations = sanitizeLocaleMap(rawLocalizations.description_localizations, 256);
		decorations.push({
			id: parsed.data.id,
			name: parsed.data.name,
			description: parsed.data.description,
			image: parsed.data.image,
			...(parsed.data.fx ? {fx: parsed.data.fx} : {}),
			accessible: parsed.data.accessible !== false,
			...(parsed.data.pro_included ? {pro_included: true} : {}),
			...(parsed.data.shopier_product_id ? {shopier_product_id: parsed.data.shopier_product_id} : {}),
			...(parsed.data.price ? {price: parsed.data.price} : {}),
			...(nameLocalizations ? {name_localizations: nameLocalizations} : {}),
			...(descriptionLocalizations ? {description_localizations: descriptionLocalizations} : {}),
		});
	}
	return decorations;
}
